import { Box, styled } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';

import { ENOPOrganType, EResourceType } from '#shared';

import { CellDeviceDrawerContent } from './CellDeviceDrawerContent';
import { CellDrawerHeader } from './CellDrawerHeader';
import { CellDrawerRoot } from './CellDrawerRoot';
import { CellHumanDrawerContent } from './CellHumanDrawerContent';
import { ToggleGroupSection } from './ToggleGroupSection';

import type { DeviceResource, HubLocation, HumanResource } from '#entities';

const organOptions: ENOPOrganType[] = [
  ENOPOrganType.Liver,
  ENOPOrganType.Heart,
  ENOPOrganType.Lungs,
];
const resourceTabs: EResourceType[] = [
  EResourceType.Device,
  EResourceType.Surgeon,
  EResourceType.Specialist,
];

export type GroupedDrawerData = {
  [EResourceType.Device]: Partial<Record<ENOPOrganType, DeviceResource[]>>;
  [EResourceType.Surgeon]: Partial<Record<ENOPOrganType, HumanResource[]>>;
  [EResourceType.Specialist]: Partial<Record<ENOPOrganType, HumanResource[]>>;
};

interface UnifiedResourceDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  location?: HubLocation;
  data?: GroupedDrawerData;
  day?: 'today' | 'tomorrow';
  organ?: ENOPOrganType;
  resourceType?: EResourceType;
}

const ToggleBox = styled(Box)(({ theme: { breakpoints, spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  marginTop: spacing(1.25),

  [breakpoints.down('xl')]: {
    marginTop: spacing(0.5),
  },
}));

export const UnifiedResourceDetails = ({
  isOpen,
  onClose,
  location,
  data,
  day,
  organ,
  resourceType,
}: UnifiedResourceDetailsProps) => {
  const [selectedResourceType, setSelectedResourceType] =
    useState<EResourceType>(resourceType ?? EResourceType.Device);
  const [selectedOrgan, setSelectedOrgan] = useState<ENOPOrganType>(
    organ ?? ENOPOrganType.Liver,
  );
  const [selectedDay, setSelectedDay] = useState<'today' | 'tomorrow'>(
    day ?? 'today',
  );

  const timeZoneAbbreviation = dayjs()
    .tz(location?.timezone ?? 'UTC')
    .format('z');
  const secondaryText = `${location?.city}, ${timeZoneAbbreviation}`;
  const currentResources = data?.[selectedResourceType]?.[selectedOrgan] ?? [];

  const handleOrganChange = (organ: ENOPOrganType) => {
    setSelectedOrgan(organ);
  };

  return (
    <CellDrawerRoot open={isOpen} anchor="right" onClose={onClose}>
      <Box>
        <CellDrawerHeader
          onClose={onClose}
          day={selectedDay}
          organ={selectedOrgan}
          secondaryText={secondaryText}
          title={
            selectedResourceType === EResourceType.Device
              ? 'Devices'
              : selectedResourceType === EResourceType.Specialist
                ? 'Specialists'
                : 'Surgeons'
          }
        />
        <ToggleBox>
          {/* Organ Type */}
          <ToggleGroupSection
            options={organOptions}
            value={selectedOrgan}
            onChange={handleOrganChange}
          />

          {/* Resource Type */}
          <ToggleGroupSection
            options={resourceTabs}
            value={selectedResourceType}
            onChange={setSelectedResourceType}
          />

          {/* Day Toggle */}
          <ToggleGroupSection
            options={['today', 'tomorrow']}
            value={selectedDay}
            onChange={val => setSelectedDay(val)}
          />
        </ToggleBox>
      </Box>

      {/* Content */}
      <Box>
        {selectedResourceType === EResourceType.Device && (
          <CellDeviceDrawerContent
            day={selectedDay}
            resources={currentResources as DeviceResource[]}
            selectedOrgan={selectedOrgan}
          />
        )}
        {selectedResourceType === EResourceType.Surgeon && (
          <CellHumanDrawerContent
            day={selectedDay}
            resources={currentResources as HumanResource[]}
            title="Surgeons"
            type={EResourceType.Surgeon}
            selectedOrgan={selectedOrgan}
          />
        )}
        {selectedResourceType === EResourceType.Specialist && (
          <CellHumanDrawerContent
            day={selectedDay}
            resources={currentResources as HumanResource[]}
            title="Specialists"
            type={EResourceType.Specialist}
            selectedOrgan={selectedOrgan}
          />
        )}
      </Box>
    </CellDrawerRoot>
  );
};
